import { useCallback, useRef, useState } from 'react'

import { useAppToast } from '../../helpers'
import { acceptAttribute } from './fileTypes'
import { IFileLimits, validatePickedFiles } from './validateFiles'

export interface IUseFilePickerOptions extends IFileLimits {
  /** Chamado com os arquivos aprovados. Nunca recebe uma lista vazia. */
  onPick: (files: File[]) => void
  /** Arquivos já escolhidos — entram na conta de duplicata, quantidade e total. */
  files?: File[]
  multiple?: boolean
  isDisabled?: boolean
}

/**
 * O miolo de um campo de upload: o `<input type="file">` escondido, a validação contra o token, a
 * recompressão de imagem grande e o toast de cada recusa.
 *
 * `<FileInput>` e `<FileDropzone>` são construídos sobre ele e cobrem o painel inteiro; o hook é o
 * escape para o punhado de lugares em que o gatilho não é nem um botão de campo nem uma área de
 * soltar — o `+` ao lado da legenda em `StudyCreditViewModal`, por exemplo. É a mesma divisão que
 * `useZipCodeLookup` faz com o `<MaskedInput>`.
 *
 * O valor do input é limpo a cada escolha de propósito: o estado do React não alcança o
 * `<input type="file">`, e sem isso reescolher o **mesmo** arquivo depois de removê-lo não dispara
 * `onChange` nenhum — o fluxo trava sem pista nenhuma justamente no caminho mais provável.
 */
export function useFilePicker({ onPick, files = [], multiple = false, isDisabled, ...limits }: IUseFilePickerOptions) {
  const toast = useAppToast()
  const inputRef = useRef<HTMLInputElement>(null)
  const [isProcessing, setIsProcessing] = useState(false)

  /**
   * A trava de concorrência é um ref, e não o `isProcessing`, porque o estado só chega na próxima
   * renderização: duas chamadas no mesmo tick — dois arrastos seguidos — leriam `false` as duas.
   *
   * Sem ela, a segunda seleção valida contra o `files` da renderização anterior e o `onPick` do
   * chamador monta a lista nova sobre essa cópia velha, jogando fora o que a primeira acabou de
   * aceitar. A janela era desprezível até a recompressão entrar: com uma foto de celular ela dura
   * os segundos em que o campo mostra "Preparando os arquivos...", que é exatamente quando alguém
   * solta o próximo arquivo.
   */
  const isProcessingRef = useRef(false)

  const handleFiles = useCallback(
    async (selected: FileList | File[] | null) => {
      // A cópia vem ANTES de limpar o input, e a ordem é o bug inteiro: `event.target.files` é a
      // `FileList` viva do elemento, não um instantâneo. Zerar o `value` esvazia essa mesma lista,
      // então limpar primeiro fazia o `Array.from` devolver `[]` — a janela fechava e não
      // acontecia nada, sem erro nenhum. Arrastar e soltar escapava porque ali a lista é a do
      // `dataTransfer`, que não pertence ao input.
      const picked = selected ? Array.from(selected) : []

      if (inputRef.current) inputRef.current.value = ''

      if (picked.length === 0 || isDisabled) return

      if (isProcessingRef.current) {
        // Recusar em silêncio seria o mesmo defeito que a `FileList` viva causava: a janela fecha
        // e nada acontece. Melhor dizer que é só esperar.
        toast.warning('Aguarde', 'Os arquivos anteriores ainda estão sendo preparados.')
        return
      }

      isProcessingRef.current = true
      setIsProcessing(true)
      try {
        const { accepted, rejected } = await validatePickedFiles(picked, limits, files)
        rejected.forEach((rejection) => toast.error(rejection.title, rejection.description))
        if (accepted.length > 0) onPick(accepted)
      } finally {
        isProcessingRef.current = false
        setIsProcessing(false)
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [files, isDisabled, limits.accept, limits.maxSizeMB, limits.maxFiles, limits.maxTotalSizeMB, onPick, toast]
  )

  const open = useCallback(() => {
    if (!isDisabled && !isProcessingRef.current) inputRef.current?.click()
  }, [isDisabled])

  /** Props do input escondido. Espalhe num `<input>` que fique montado junto do gatilho. */
  const inputProps = {
    ref: inputRef,
    type: 'file' as const,
    accept: acceptAttribute(limits.accept),
    multiple,
    disabled: isDisabled,
    style: { display: 'none' },
    onChange: (event: React.ChangeEvent<HTMLInputElement>) => handleFiles(event.target.files)
  }

  return { open, inputProps, handleFiles, isProcessing }
}
